import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingButton({
  label = "Book a Free Consultation",
  meetingType = "consultation",
  variant = "primary",
  testId = "booking-open",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    preferred_date: "", preferred_time: "", timezone_str: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const cls = variant === "primary" ? "dm-btn-primary" : variant === "ghost" ? "dm-btn-ghost" : "dm-btn-outline";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, meeting_type: meetingType });
      toast.success(meetingType === "consultation" ? "Consultation requested — calendar invite on the way." : "Discovery call requested — a consultant will confirm shortly.");
      setOpen(false);
      setForm({ name: "", email: "", company: "", phone: "", preferred_date: "", preferred_time: "", timezone_str: Intl.DateTimeFormat().resolvedOptions().timeZone, notes: "" });
    } catch { toast.error("Booking failed. Please try again."); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white border border-[var(--dm-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--dm-emerald)]";
  const labelCls = "block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--dm-muted)] mb-2";

  return (
    <>
      <button data-testid={testId} onClick={() => setOpen(true)} className={`${cls} ${className}`}>
        <Calendar className="w-4 h-4" /> {label}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="booking-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[var(--dm-border)]" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[var(--dm-border)] bg-[var(--dm-navy)] text-white">
              <div>
                <div className="dm-overline text-white/70">{meetingType === "consultation" ? "Free consultation" : "Discovery call"}</div>
                <div className="font-serif text-xl">{label}</div>
              </div>
              <button data-testid="booking-close" onClick={() => setOpen(false)} className="p-2 hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Full name</label><input required data-testid="bk-name" className={inputCls} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
                <div><label className={labelCls}>Work email</label><input required type="email" data-testid="bk-email" className={inputCls} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Company</label><input data-testid="bk-company" className={inputCls} value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} /></div>
                <div><label className={labelCls}>Phone</label><input data-testid="bk-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Preferred date</label><input required type="date" data-testid="bk-date" className={inputCls} value={form.preferred_date} onChange={(e) => setForm({...form, preferred_date: e.target.value})} /></div>
                <div><label className={labelCls}>Preferred time</label><input required type="time" data-testid="bk-time" className={inputCls} value={form.preferred_time} onChange={(e) => setForm({...form, preferred_time: e.target.value})} /></div>
                <div><label className={labelCls}>Timezone</label><input data-testid="bk-tz" className={inputCls} value={form.timezone_str} onChange={(e) => setForm({...form, timezone_str: e.target.value})} /></div>
              </div>
              <div>
                <label className={labelCls}>Anything we should know?</label>
                <textarea rows={3} data-testid="bk-notes" className={inputCls} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={loading} data-testid="bk-submit" className="dm-btn-primary">
                  {loading ? "Booking…" : "Confirm booking"}
                </button>
                <p className="text-xs text-[var(--dm-muted)]">You'll receive a calendar invitation via email.</p>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
